import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { WorkflowTemplateOptionFragment } from 'types/graphql'

import { useWorkflowTemplatesQuery } from 'src/fetch/recordTemplates'
import { DateFormats, formatDate } from 'src/utils/date'

export const getOptionFromWorkflowTemplate = (wt: WorkflowTemplateOptionFragment) => {
  return {
    label: wt.name ?? `Created at ${formatDate(wt.createdAt ?? '', DateFormats.MonthNameDateTime)}`,
    value: wt,
  }
}

type Props = {
  isRequired?: boolean
  label?: string
  onValueChange: (value: WorkflowTemplateOptionFragment | undefined) => void
  recordTemplateId?: number
  value: WorkflowTemplateOptionFragment | undefined
  errorMessage?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}
const WorkflowTemplateSelect = ({
  errorMessage,
  isRequired,
  label,
  onValueChange,
  recordTemplateId,
  value,
  onBlur,
}: Props) => {
  const { workflowTemplates, isLoading: isLoadingOptions } = useWorkflowTemplatesQuery({
    recordTemplateId,
  })

  const options: SelectOption<WorkflowTemplateOptionFragment>[] = useMemo(
    () => workflowTemplates?.map((wt) => getOptionFromWorkflowTemplate(wt)) ?? [],
    [workflowTemplates]
  )
  const selectedOption = useMemo(
    () => (value ? getOptionFromWorkflowTemplate(value) : undefined),
    [value]
  )

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<WorkflowTemplateOptionFragment> | undefined) => {
      onValueChange(option?.value)
    },
    [onValueChange]
  )

  return (
    <Select
      getOptionKey={(o) => o.value.id}
      isLoading={isLoadingOptions}
      isRequired={isRequired}
      label={label}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder="Select a workflow template"
      selectedOption={selectedOption}
      errorMessage={errorMessage}
      isInvalid={!!errorMessage}
      onBlur={onBlur}
    />
  )
}

export default React.memo(WorkflowTemplateSelect)
